import React, { useCallback, useEffect, useState } from 'react';
import {
    Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, TextField, Typography,
} from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { BuyerService } from '../../services/buyer.service';
import { IBuyer, IBuyerAttachment, ICreateBuyerRequest } from '../../interfaces/garments/buyer.interface';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS } from '../../constants/permissions';
import BuyerAttachments from './BuyerAttachments';
import CommercialPartyAutocomplete from '../commercial/CommercialPartyAutocomplete';

interface BuyerFormDialogProps {
    open: boolean;
    buyer?: IBuyer | null;
    onClose: () => void;
    onSaved: () => void;
}

// Mirrors `CreateBuyerRequest`'s @Size bounds exactly (backend/.../merch/dataclass/CreateBuyerRequest.java).
const MAX = {
    code: 40, name: 255, buyingHouse: 255, countryCode: 2, contactPerson: 255, email: 255,
    phone: 40, address: 1000, paymentTerms: 255, incoterm: 20, complianceStandard: 255,
    currencyCode: 3, notes: 1000, auditStandard: 64, cocVersion: 64, complianceNotes: 2000,
    nominatedSuppliers: 2000,
};

// Suggested audit standards (spec §2, 7.5) — the field stays freeSolo so any other value can be typed.
const AUDIT_STANDARD_OPTIONS = ['BSCI', 'SEDEX-SMETA', 'WRAP', 'RSC', 'Higg FSLM', 'ISO 45001', 'amfori'];

interface FormState {
    code: string;
    name: string;
    buyingHouse: string;
    countryCode: string;
    contactPerson: string;
    email: string;
    phone: string;
    address: string;
    paymentTerms: string;
    incoterm: string;
    complianceStandard: string;
    currencyCode: string;
    notes: string;
    auditStandard: string;
    cocVersion: string;
    cocAcceptedOn: string;
    cocExpiresOn: string;
    complianceNotes: string;
    nominatedSuppliers: string;
    agentPartyId: string | null;
}

const emptyForm: FormState = {
    code: '', name: '', buyingHouse: '', countryCode: '', contactPerson: '', email: '', phone: '',
    address: '', paymentTerms: '', incoterm: '', complianceStandard: '', currencyCode: '', notes: '',
    auditStandard: '', cocVersion: '', cocAcceptedOn: '', cocExpiresOn: '', complianceNotes: '',
    nominatedSuppliers: '', agentPartyId: null,
};

/** Create/edit dialog for a buyer (garments-merchandising T8 + compliance profile, spec 7.5). No
 *  delete affordance — buyers are referenced master data; `BuyerList` offers Activate/Deactivate
 *  instead. Attachments (CoC/audit/certificate documents) are mounted here, in the edit dialog,
 *  since they need a persisted `buyerId` — they only show up once the buyer has been saved. */
export function BuyerFormDialog({ open, buyer, onClose, onSaved }: BuyerFormDialogProps) {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManageAttachments = hasPermission(PERMISSIONS.BUYER_MANAGE);
    const isEdit = Boolean(buyer);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    // Buying House is legacy free text (superseded by the agentPartyId picker) -- collapsed unless
    // it already carries a value or the user explicitly expands it.
    const [showLegacyBuyingHouse, setShowLegacyBuyingHouse] = useState(false);

    const [attachments, setAttachments] = useState<IBuyerAttachment[]>([]);
    const [attachmentsLoading, setAttachmentsLoading] = useState(false);

    const institutionId = AuthService.getSelectedInstitution() ?? '';

    useEffect(() => {
        if (!open) return;
        setForm(buyer ? {
            code: buyer.code,
            name: buyer.name,
            buyingHouse: buyer.buyingHouse ?? '',
            countryCode: buyer.countryCode ?? '',
            contactPerson: buyer.contactPerson ?? '',
            email: buyer.email ?? '',
            phone: buyer.phone ?? '',
            address: buyer.address ?? '',
            paymentTerms: buyer.paymentTerms ?? '',
            incoterm: buyer.incoterm ?? '',
            complianceStandard: buyer.complianceStandard ?? '',
            currencyCode: buyer.currencyCode ?? '',
            notes: buyer.notes ?? '',
            auditStandard: buyer.auditStandard ?? '',
            cocVersion: buyer.cocVersion ?? '',
            cocAcceptedOn: buyer.cocAcceptedOn ?? '',
            cocExpiresOn: buyer.cocExpiresOn ?? '',
            complianceNotes: buyer.complianceNotes ?? '',
            nominatedSuppliers: buyer.nominatedSuppliers ?? '',
            agentPartyId: buyer.agentPartyId ?? null,
        } : emptyForm);
        setErrors({});
        setShowLegacyBuyingHouse(!!buyer?.buyingHouse);
    }, [open, buyer]);

    const loadAttachments = useCallback(async () => {
        if (!buyer) return;
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setAttachmentsLoading(true);
        try {
            const list = await BuyerService.listBuyerAttachments(institutionId, buyer.id);
            setAttachments(list);
        } catch (e) {
            toast.showApiError(e, 'Failed to load attachments');
        } finally {
            setAttachmentsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyer?.id]);

    useEffect(() => {
        if (open && buyer) {
            loadAttachments();
        } else {
            setAttachments([]);
        }
    }, [open, buyer, loadAttachments]);

    const setField = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(prev => ({ ...prev, [field]: e.target.value }));

    const validate = (): Record<string, string> => {
        const next: Record<string, string> = {};
        if (!form.code.trim()) next.code = 'Code is required';
        if (!form.name.trim()) next.name = 'Name is required';
        if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) next.email = 'Enter a valid email';
        if (form.cocAcceptedOn && form.cocExpiresOn && form.cocExpiresOn < form.cocAcceptedOn) {
            next.cocExpiresOn = 'Expiry date must be on or after the accepted date';
        }
        return next;
    };

    const handleClose = () => {
        if (saving) return;
        onClose();
    };

    const handleSubmit = async () => {
        const validationErrors = validate();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;

        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;

        const request: ICreateBuyerRequest = {
            code: form.code.trim(),
            name: form.name.trim(),
            buyingHouse: form.buyingHouse.trim() || null,
            countryCode: form.countryCode.trim() || null,
            contactPerson: form.contactPerson.trim() || null,
            email: form.email.trim() || null,
            phone: form.phone.trim() || null,
            address: form.address.trim() || null,
            paymentTerms: form.paymentTerms.trim() || null,
            incoterm: form.incoterm.trim() || null,
            complianceStandard: form.complianceStandard.trim() || null,
            currencyCode: form.currencyCode.trim() || null,
            notes: form.notes.trim() || null,
            auditStandard: form.auditStandard.trim() || null,
            cocVersion: form.cocVersion.trim() || null,
            cocAcceptedOn: form.cocAcceptedOn || null,
            cocExpiresOn: form.cocExpiresOn || null,
            complianceNotes: form.complianceNotes.trim() || null,
            nominatedSuppliers: form.nominatedSuppliers.trim() || null,
            // Fix round 1: always send the current agentPartyId -- the backend now only
            // re-validates it when it actually changed from the persisted value (mirrors the
            // buyerId/styleId only-on-change precedent), so an unchanged value never 400s even if
            // the referenced agent has since been deactivated.
            agentPartyId: form.agentPartyId,
        };

        setSaving(true);
        try {
            if (isEdit && buyer) {
                await BuyerService.update(institutionId, buyer.id, request);
                toast.showSuccess('Buyer updated');
            } else {
                await BuyerService.create(institutionId, request);
                toast.showSuccess('Buyer created');
            }
            onSaved();
            onClose();
        } catch (e) {
            toast.showApiError(e, isEdit ? 'Failed to update buyer' : 'Failed to create buyer');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ 'data-help-page': 'merchandising.buyerForm.page' } as any}>
            <DialogTitle>{isEdit ? 'Edit Buyer' : 'New Buyer'}</DialogTitle>
            <DialogContent>
                <Box data-help-section="merchandising.buyerForm.section.details" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            data-help="merchandising.buyerForm.field.code"
                            label="Code" value={form.code} onChange={setField('code')}
                            error={!!errors.code} helperText={errors.code}
                            inputProps={{ maxLength: MAX.code }} required fullWidth
                        />
                        <TextField
                            data-help="merchandising.buyerForm.field.name"
                            label="Name" value={form.name} onChange={setField('name')}
                            error={!!errors.name} helperText={errors.name}
                            inputProps={{ maxLength: MAX.name }} required fullWidth
                        />
                    </Box>
                    <CommercialPartyAutocomplete
                        data-help="merchandising.buyerForm.field.agent"
                        institutionId={institutionId}
                        partyTypes={['BUYING_AGENT']}
                        label="Buying agent (default)"
                        value={form.agentPartyId}
                        onChange={party => setForm(prev => ({ ...prev, agentPartyId: party?.id ?? null }))}
                    />
                    {showLegacyBuyingHouse ? (
                        <TextField
                            data-help="merchandising.buyerForm.field.buyingHouse"
                            label="Buying house (typed name)" value={form.buyingHouse} onChange={setField('buyingHouse')}
                            helperText="Older free-text entry. Prefer choosing a buying agent above."
                            inputProps={{ maxLength: MAX.buyingHouse }} fullWidth
                        />
                    ) : (
                        <Button data-help="merchandising.buyerForm.showBuyingHouse" size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => setShowLegacyBuyingHouse(true)}>
                            Show typed buying house
                        </Button>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            data-help="merchandising.buyerForm.field.countryCode"
                            label="Country Code" value={form.countryCode} onChange={setField('countryCode')}
                            placeholder="BD" inputProps={{ maxLength: MAX.countryCode }} fullWidth
                        />
                        <TextField
                            data-help="merchandising.buyerForm.field.currencyCode"
                            label="Currency Code" value={form.currencyCode} onChange={setField('currencyCode')}
                            placeholder="USD" inputProps={{ maxLength: MAX.currencyCode }} fullWidth
                        />
                    </Box>
                    <TextField
                        data-help="merchandising.buyerForm.field.contactPerson"
                        label="Contact Person" value={form.contactPerson} onChange={setField('contactPerson')}
                        inputProps={{ maxLength: MAX.contactPerson }} fullWidth
                    />
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            data-help="merchandising.buyerForm.field.email"
                            label="Email" value={form.email} onChange={setField('email')}
                            error={!!errors.email} helperText={errors.email}
                            inputProps={{ maxLength: MAX.email }} fullWidth
                        />
                        <TextField
                            data-help="merchandising.buyerForm.field.phone"
                            label="Phone" value={form.phone} onChange={setField('phone')}
                            inputProps={{ maxLength: MAX.phone }} fullWidth
                        />
                    </Box>
                    <TextField
                        data-help="merchandising.buyerForm.field.address"
                        label="Address" value={form.address} onChange={setField('address')}
                        inputProps={{ maxLength: MAX.address }} multiline minRows={2} fullWidth
                    />
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            data-help="merchandising.buyerForm.field.paymentTerms"
                            label="Payment Terms" value={form.paymentTerms} onChange={setField('paymentTerms')}
                            inputProps={{ maxLength: MAX.paymentTerms }} fullWidth
                        />
                        <TextField
                            data-help="merchandising.buyerForm.field.incoterm"
                            label="Incoterm" value={form.incoterm} onChange={setField('incoterm')}
                            placeholder="FOB" inputProps={{ maxLength: MAX.incoterm }} fullWidth
                        />
                    </Box>
                    {!!form.complianceStandard && (
                        <TextField
                            data-help="merchandising.buyerForm.field.complianceStandard"
                            label="Compliance standard" value={form.complianceStandard} onChange={setField('complianceStandard')}
                            inputProps={{ maxLength: MAX.complianceStandard }} fullWidth
                        />
                    )}
                    <TextField
                        data-help="merchandising.buyerForm.field.notes"
                        label="Notes" value={form.notes} onChange={setField('notes')}
                        inputProps={{ maxLength: MAX.notes }} multiline minRows={2} fullWidth
                    />

                    <Divider sx={{ mt: 1 }} />
                    <Typography variant="subtitle1">Compliance</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Autocomplete
                            freeSolo
                            fullWidth
                            options={AUDIT_STANDARD_OPTIONS}
                            inputValue={form.auditStandard}
                            onInputChange={(_, value) => setForm(prev => ({ ...prev, auditStandard: value.slice(0, MAX.auditStandard) }))}
                            renderInput={(params) => (
                                <TextField {...params} data-help="merchandising.buyerForm.field.auditStandard" label="Audit Standard" inputProps={{ ...params.inputProps, maxLength: MAX.auditStandard }} />
                            )}
                        />
                        <TextField
                            data-help="merchandising.buyerForm.field.cocVersion"
                            label="CoC Version" value={form.cocVersion} onChange={setField('cocVersion')}
                            inputProps={{ maxLength: MAX.cocVersion }} fullWidth
                        />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            data-help="merchandising.buyerForm.field.cocAcceptedOn"
                            label="CoC Accepted On" type="date" value={form.cocAcceptedOn} onChange={setField('cocAcceptedOn')}
                            InputLabelProps={{ shrink: true }} fullWidth
                        />
                        <TextField
                            data-help="merchandising.buyerForm.field.cocExpiresOn"
                            label="CoC Expires On" type="date" value={form.cocExpiresOn} onChange={setField('cocExpiresOn')}
                            error={!!errors.cocExpiresOn} helperText={errors.cocExpiresOn}
                            InputLabelProps={{ shrink: true }} fullWidth
                        />
                    </Box>
                    <TextField
                        data-help="merchandising.buyerForm.field.complianceNotes"
                        label="Compliance Notes" value={form.complianceNotes} onChange={setField('complianceNotes')}
                        inputProps={{ maxLength: MAX.complianceNotes }} multiline minRows={2} fullWidth
                    />
                    <TextField
                        data-help="merchandising.buyerForm.field.nominatedSuppliers"
                        label="Nominated Suppliers" value={form.nominatedSuppliers} onChange={setField('nominatedSuppliers')}
                        helperText="One supplier per line"
                        inputProps={{ maxLength: MAX.nominatedSuppliers }} multiline minRows={2} fullWidth
                    />

                    {isEdit && buyer ? (
                        <>
                            <Divider sx={{ mt: 1 }} />
                            {attachmentsLoading ? <CircularProgress size={24} /> : (
                                <BuyerAttachments
                                    buyerId={buyer.id}
                                    attachments={attachments}
                                    canManage={canManageAttachments}
                                    onChanged={loadAttachments}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            <Divider sx={{ mt: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                Save the buyer first to attach CoC/audit documents.
                            </Typography>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button data-help="merchandising.buyerForm.cancel" onClick={handleClose} disabled={saving}>Cancel</Button>
                <Button data-help="merchandising.buyerForm.save" variant="contained" onClick={handleSubmit} disabled={saving}>
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default BuyerFormDialog;
